import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type PageSizeOption = 10 | 20 | 50;
export const PAGE_SIZE_OPTIONS: readonly PageSizeOption[] = [10, 20, 50] as const;

export interface PaginationController {
  /** 0-indexed current page */
  page: number;
  /** Number of rows per page (10, 20, or 50) */
  pageSize: PageSizeOption;
  /** Total number of items across all pages */
  totalItems: number;
  /** Total number of pages (at least 1) */
  totalPages: number;
  /** 1-indexed start item number (0 when totalItems === 0) */
  startIndex: number;
  /** 1-indexed end item number (0 when totalItems === 0) */
  endIndex: number;
  /** Number of lookahead pages (up to 2: page+1, page+2) already prefetched and ready */
  prefetchedPagesReady: number;
  /** Whether background lookahead prefetching is currently in flight */
  prefetching?: boolean;
  /** Set 0-indexed page */
  setPage: (nextPage: number | ((prev: number) => number)) => void;
  /** Change page size (10 | 20 | 50) and reset to page 0 */
  setPageSize: (nextSize: PageSizeOption) => void;
}

export interface UsePaginatedListResult<T> extends PaginationController {
  /** Slice of items for the active page */
  pageItems: T[];
  /** Pre-computed lookahead slices for up to 2 pages ahead ([page+1, page+2]) */
  prefetchedNextPages: T[][];
}

/**
 * Client/hybrid pagination hook with default pageSize = 10 (options 10, 20, 50)
 * and pre-computed 2-page lookahead slices (`page + 1` and `page + 2`).
 */
export function usePaginatedList<T>(
  items: readonly T[],
  defaultPageSize: PageSizeOption = 10,
): UsePaginatedListResult<T> {
  const [page, setPageState] = useState<number>(0);
  const [pageSize, setPageSizeState] = useState<PageSizeOption>(defaultPageSize);

  const totalItems = items.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(Math.max(0, page), totalPages - 1);

  useEffect(() => {
    if (page !== safePage) {
      setPageState(safePage);
    }
  }, [page, safePage]);

  const { pageItems, prefetchedNextPages } = useMemo(() => {
    const start = safePage * pageSize;
    const currentSlice = items.slice(start, start + pageSize);

    const nextSlices: T[][] = [];
    for (let offset = 1; offset <= 2; offset++) {
      const nextPage = safePage + offset;
      if (nextPage < totalPages && nextPage * pageSize < totalItems) {
        const nextStart = nextPage * pageSize;
        nextSlices.push(items.slice(nextStart, nextStart + pageSize));
      }
    }

    return {
      pageItems: currentSlice,
      prefetchedNextPages: nextSlices,
    };
  }, [items, safePage, pageSize, totalPages, totalItems]);

  const setPage = useCallback(
    (next: number | ((prev: number) => number)) => {
      setPageState((prev) => {
        const resolved = typeof next === 'function' ? next(prev) : next;
        return Math.min(Math.max(0, resolved), Math.max(0, Math.ceil(items.length / pageSize) - 1));
      });
    },
    [items.length, pageSize],
  );

  const setPageSize = useCallback((nextSize: PageSizeOption) => {
    setPageSizeState(nextSize);
    setPageState(0);
  }, []);

  const startIndex = totalItems === 0 ? 0 : safePage * pageSize + 1;
  const endIndex = totalItems === 0 ? 0 : Math.min(totalItems, (safePage + 1) * pageSize);

  return {
    page: safePage,
    pageSize,
    totalItems,
    totalPages,
    startIndex,
    endIndex,
    pageItems,
    prefetchedNextPages,
    prefetchedPagesReady: prefetchedNextPages.length,
    prefetching: false,
    setPage,
    setPageSize,
  };
}

export interface ServerPageResponse<T> {
  data: T[];
  total: number;
}

export interface UseServerPaginatedQueryOptions<T> {
  /** Unique key representing the endpoint + active filter/search state */
  queryKey: string;
  /** Function that fetches a specific 0-indexed page with the given pageSize */
  fetchPage: (page: number, pageSize: PageSizeOption) => Promise<ServerPageResponse<T>>;
  /** Initial page size (defaults to 10) */
  defaultPageSize?: PageSizeOption;
  /** Whether the query should execute automatically (defaults to true) */
  enabled?: boolean;
}

export interface UseServerPaginatedQueryResult<T> extends PaginationController {
  data: T[];
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

interface CacheEntry<T> {
  data: T[];
  total: number;
  fetchedAt: number;
}

export function useDebouncedValue<T>(value: T, delayMs = 250): T {
  const [debounced, setDebounced] = useState<T>(value);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebounced(value);
    }, delayMs);
    return () => {
      window.clearTimeout(timer);
    };
  }, [value, delayMs]);

  return debounced;
}

/**
 * Server-paginated query hook with default pageSize = 10 (options 10, 20, 50)
 * and automatic 2-page lookahead prefetching (`page + 1` and `page + 2`).
 */
export function useServerPaginatedQuery<T>({
  queryKey,
  fetchPage,
  defaultPageSize = 10,
  enabled = true,
}: UseServerPaginatedQueryOptions<T>): UseServerPaginatedQueryResult<T> {
  const [page, setPageState] = useState<number>(0);
  const [pageSize, setPageSizeState] = useState<PageSizeOption>(defaultPageSize);
  const [data, setData] = useState<T[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(enabled);
  const [prefetching, setPrefetching] = useState<boolean>(false);
  const [prefetchedPagesReady, setPrefetchedPagesReady] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [reloadNonce, setReloadNonce] = useState<number>(0);

  const cacheRef = useRef<Map<string, CacheEntry<T>>>(new Map());
  const inFlightRef = useRef<Map<string, Promise<ServerPageResponse<T>>>>(new Map());
  const fetchPageRef = useRef(fetchPage);
  fetchPageRef.current = fetchPage;

  const prevQueryKeyRef = useRef<string>(queryKey);
  let activePage = page;
  if (prevQueryKeyRef.current !== queryKey) {
    prevQueryKeyRef.current = queryKey;
    activePage = 0;
    if (page !== 0) {
      setPageState(0);
    }
  }

  const makeCacheKey = useCallback(
    (targetPage: number, targetSize: PageSizeOption) => `${queryKey}::${targetSize}::${targetPage}`,
    [queryKey],
  );

  const countPrefetchedReady = useCallback(
    (basePage: number, size: PageSizeOption, total: number) => {
      let ready = 0;
      for (let offset = 1; offset <= 2; offset++) {
        const nextP = basePage + offset;
        if (nextP * size < total && cacheRef.current.has(`${queryKey}::${size}::${nextP}`)) {
          ready += 1;
        }
      }
      return ready;
    },
    [queryKey],
  );

  const fetchWithCache = useCallback(
    async (targetPage: number, targetSize: PageSizeOption): Promise<ServerPageResponse<T>> => {
      const key = makeCacheKey(targetPage, targetSize);
      const cached = cacheRef.current.get(key);
      if (cached) {
        return { data: cached.data, total: cached.total };
      }

      const existingPromise = inFlightRef.current.get(key);
      if (existingPromise) {
        return existingPromise;
      }

      const promise = fetchPageRef
        .current(targetPage, targetSize)
        .then((res) => {
          const normalized: ServerPageResponse<T> = {
            data: Array.isArray(res.data) ? res.data : [],
            total: typeof res.total === 'number' ? res.total : (Array.isArray(res.data) ? res.data.length : 0),
          };
          cacheRef.current.set(key, {
            data: normalized.data,
            total: normalized.total,
            fetchedAt: Date.now(),
          });
          inFlightRef.current.delete(key);
          return normalized;
        })
        .catch((err) => {
          inFlightRef.current.delete(key);
          throw err;
        });

      inFlightRef.current.set(key, promise);
      return promise;
    },
    [makeCacheKey],
  );

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    const activeKey = makeCacheKey(activePage, pageSize);
    const cached = cacheRef.current.get(activeKey);

    if (cached) {
      setData(cached.data);
      setTotalItems(cached.total);
      setLoading(false);
      setError(null);
      setPrefetchedPagesReady(countPrefetchedReady(activePage, pageSize, cached.total));
    } else {
      setLoading(true);
      setError(null);
    }

    (async () => {
      try {
        const res = cached ?? (await fetchWithCache(activePage, pageSize));
        if (cancelled) return;

        setData(res.data);
        setTotalItems(res.total);
        setLoading(false);

        // Prefetch up to 2 pages ahead (activePage + 1 and activePage + 2)
        const pagesToPrefetch: number[] = [];
        for (let offset = 1; offset <= 2; offset++) {
          const nextPage = activePage + offset;
          if (nextPage * pageSize < res.total) {
            pagesToPrefetch.push(nextPage);
          }
        }

        setPrefetchedPagesReady(countPrefetchedReady(activePage, pageSize, res.total));

        const missingPrefetchPages = pagesToPrefetch.filter(
          (p) => !cacheRef.current.has(makeCacheKey(p, pageSize)),
        );

        if (missingPrefetchPages.length > 0) {
          setPrefetching(true);
          await Promise.allSettled(
            missingPrefetchPages.map((p) => fetchWithCache(p, pageSize)),
          );
          if (!cancelled) {
            setPrefetching(false);
            setPrefetchedPagesReady(countPrefetchedReady(activePage, pageSize, res.total));
          }
        } else {
          setPrefetching(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load data');
          setLoading(false);
          setPrefetching(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, activePage, pageSize, queryKey, reloadNonce, makeCacheKey, fetchWithCache, countPrefetchedReady]);

  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  const setPage = useCallback(
    (next: number | ((prev: number) => number)) => {
      setPageState((prev) => {
        const resolved = typeof next === 'function' ? next(prev) : next;
        const maxPage = Math.max(0, Math.ceil(totalItems / pageSize) - 1);
        return Math.min(Math.max(0, resolved), maxPage);
      });
    },
    [totalItems, pageSize],
  );

  const setPageSize = useCallback((nextSize: PageSizeOption) => {
    setPageSizeState(nextSize);
    setPageState(0);
  }, []);

  const reload = useCallback(async () => {
    // Clear cached pages for the active queryKey so fresh data + lookahead pages are fetched
    for (const key of Array.from(cacheRef.current.keys())) {
      if (key.startsWith(`${queryKey}::`)) {
        cacheRef.current.delete(key);
      }
    }
    setReloadNonce((n) => n + 1);
  }, [queryKey]);

  const activeCacheEntry = cacheRef.current.get(makeCacheKey(activePage, pageSize));
  const effectiveLoading = loading || (enabled && !activeCacheEntry);
  const effectiveData = activeCacheEntry ? activeCacheEntry.data : data;
  const effectiveTotal = activeCacheEntry ? activeCacheEntry.total : totalItems;

  const startIndex = effectiveTotal === 0 ? 0 : activePage * pageSize + 1;
  const endIndex = effectiveTotal === 0 ? 0 : Math.min(effectiveTotal, (activePage + 1) * pageSize);

  return {
    page: activePage,
    pageSize,
    totalItems: effectiveTotal,
    totalPages: Math.max(1, Math.ceil(effectiveTotal / pageSize)),
    startIndex,
    endIndex,
    prefetchedPagesReady,
    prefetching,
    data: effectiveData,
    loading: effectiveLoading,
    error,
    setPage,
    setPageSize,
    reload,
  };
}
